import {
  Radio,
  RadioGroup,
  Spinner,
  Switch,
  Tag,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { gql, useQuery } from "@apollo/client";
import {
  BroadcastManyQuery,
  EnumBroadcastSharedToSentToPeopleSystemRoles,
  EnumBroadcastSharedToType,
  EnumBroadcastStatus,
  EnumOwnerType,
  useBroadcastUpdateByIdMutation,
} from "gql/generated";
import { SendDiagonal } from "iconoir-react";
import _ from "lodash";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SimpleFlag, WhereaboutsModal } from "shared/components";
import { TextEditor } from "shared/components/molecules/TextEditor";
import { usePeopleTagsContext } from "shared/components/organisms/PeopleTags";
import { usePeopleContext } from "shared/contextProviders";
import { useFeatureFlag } from "shared/contextProviders/featureFlags";
import { FeatureFlags } from "shared/featureFlags";
import { getMultilingualString } from "shared/utils";
import { Flex } from "styled-system/jsx";
import { usePostHog } from "shared/hooks";

const TEST_USERS_QUERY = gql`
  query ShareBroadcastTestUsers($organizationId: String!) {
    testRecordsByOrganization(organizationId: $organizationId) {
      count
      testTagId
    }
  }
`;

const STAFF_ROLE = EnumBroadcastSharedToSentToPeopleSystemRoles.DmoStaff;

type ShareBroadcastModalProps = {
  isOpen: boolean;
  onClose: () => void;
  broadcast?: BroadcastManyQuery["broadcastMany"][0];
  onShareSuccess: () => void;
};

export const ShareBroadcastModal = ({
  isOpen,
  onClose,
  broadcast,
  onShareSuccess,
}: ShareBroadcastModalProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.BROADCAST.MODAL.SHARE_MODAL",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();

  const testDataEnabled = useFeatureFlag(FeatureFlags.OnboardingTestDataTool);
  const ownerOrganizationId =
    broadcast?.owner?.type === EnumOwnerType.Organization
      ? String(broadcast.owner.entityId)
      : "";
  const { data: testRecordsData } = useQuery<{
    testRecordsByOrganization: { count: number; testTagId?: string | null };
  }>(TEST_USERS_QUERY, {
    variables: { organizationId: ownerOrganizationId },
    skip: !isOpen || !ownerOrganizationId || !testDataEnabled,
    errorPolicy: "ignore",
    fetchPolicy: "cache-and-network",
  });
  const testSummary = testDataEnabled
    ? testRecordsData?.testRecordsByOrganization
    : undefined;
  const testUserCount = testSummary?.count ?? 0;
  const testTagId = testSummary?.testTagId ?? "";

  const { initialLoad, people } = usePeopleContext();
  const {
    systemRoles: systemRoleOptions,
    allTagGroupsWithTags,
    membershipTierTags,
  } = usePeopleTagsContext();

  // Only people with an active Whereabouts account (Enrolled = has logged in)
  // align with backend broadcast eligibility
  const allWhereaboutsPeopleUsers = useMemo(
    () => people.filter(person => person.isUserClaimed),
    [people],
  );

  // Merge tags from two sources:
  // 1. Tags already assigned to enrolled people (people-derived)
  // 2. All catalog tags (person tags + inheritable tags from PeopleTagsContext)
  // This ensures inheritable tags (e.g. membership tiers) appear even when not
  // yet materialized onto any enrolled person.
  const visiblePeopleTags = useMemo(
    () =>
      _.uniqBy(
        [
          ...allWhereaboutsPeopleUsers.flatMap(person => person.tags),
          ...allTagGroupsWithTags.flatMap(g => g.tags),
          ...membershipTierTags,
        ],
        "_id",
      ).filter(tag => tag._id !== testTagId),
    [allWhereaboutsPeopleUsers, allTagGroupsWithTags, membershipTierTags, testTagId],
  );

  // System roles that would match at least one eligible person. Hide Whereabouts Users (redundant in share settings).
  const visibleSystemRoles = useMemo(
    () =>
      systemRoleOptions
        .filter(
          role =>
            role.id !== EnumBroadcastSharedToSentToPeopleSystemRoles.WhereaboutsUser,
        )
        .filter(role =>
          allWhereaboutsPeopleUsers.some(person =>
            person.systemRoles?.some(
              sr =>
                sr != null &&
                (sr as unknown as EnumBroadcastSharedToSentToPeopleSystemRoles) ===
                  role.id,
            ),
          ),
        ),
    [systemRoleOptions, allWhereaboutsPeopleUsers],
  );

  const defaultSendToType = useMemo(() => {
    return broadcast?.sharedTo
      ? broadcast?.sharedTo?.type === EnumBroadcastSharedToType.AllPeople
        ? "ALL"
        : "BY_TAG"
      : "ALL";
  }, [broadcast]);

  const defaultSelectedPeopleTags = useMemo(() => {
    return broadcast?.sharedTo?.sentToPeopleTags ?? [];
  }, [broadcast]);

  const defaultSelectedSystemRoleIds = useMemo(
    (): EnumBroadcastSharedToSentToPeopleSystemRoles[] =>
      (broadcast?.sharedTo?.sentToPeopleSystemRoles ?? []).filter(
        (r): r is EnumBroadcastSharedToSentToPeopleSystemRoles => r != null,
      ),
    [broadcast],
  );

  const [sendToType, setSendToType] = useState<"ALL" | "BY_TAG">(
    defaultSendToType,
  );
  const [selectedPeopleTags, setSelectedPeopleTags] = useState<string[]>(
    defaultSelectedPeopleTags,
  );
  const [selectedSystemRoleIds, setSelectedSystemRoleIds] = useState<
    EnumBroadcastSharedToSentToPeopleSystemRoles[]
  >(defaultSelectedSystemRoleIds);
  const [skipEmails, setSkipEmails] = useState(false);

  const teaserEnabled = useFeatureFlag(FeatureFlags.BroadcastShareTeaser);
  const [isTeaserActive, setIsTeaserActive] = useState(
    broadcast?.sharedTo?.teaser?.enabled ?? false,
  );
  const [teaserDescription, setTeaserDescription] = useState(
    broadcast?.sharedTo?.teaser?.description
      ? getMultilingualString(broadcast.sharedTo.teaser.description)
      : "",
  );

  // Initialize the selected people tags, system roles, skip emails and send to type when
  // the broadcast is loaded
  useEffect(() => {
    if (broadcast && broadcast.sharedTo) {
      setSendToType(
        broadcast.sharedTo.type === EnumBroadcastSharedToType.AllPeople
          ? "ALL"
          : "BY_TAG",
      );
      setSelectedPeopleTags(broadcast.sharedTo.sentToPeopleTags ?? []);
      setSelectedSystemRoleIds(
        (broadcast.sharedTo.sentToPeopleSystemRoles ?? []).filter(
          (r): r is EnumBroadcastSharedToSentToPeopleSystemRoles => r != null,
        ),
      );
      setSkipEmails(broadcast.sharedTo?.skipEmails ?? false);
      setIsTeaserActive(broadcast.sharedTo?.teaser?.enabled ?? false);
      setTeaserDescription(
        broadcast.sharedTo?.teaser?.description
          ? getMultilingualString(broadcast.sharedTo.teaser.description)
          : "",
      );
    }
  }, [broadcast]);

  const filteredPeopleBySelectedAudienceCount = useMemo(
    () =>
      allWhereaboutsPeopleUsers.filter(
        person =>
          person.tags.some(tag => selectedPeopleTags.includes(tag._id)) ||
          (person.systemRoles?.some(
            sr =>
              sr != null &&
              selectedSystemRoleIds.includes(
                sr as unknown as EnumBroadcastSharedToSentToPeopleSystemRoles,
              ),
          ) ?? false),
      ).length,
    [
      allWhereaboutsPeopleUsers,
      selectedPeopleTags,
      selectedSystemRoleIds,
    ],
  );

  const isTestUsersSelected = !!testTagId && selectedPeopleTags.includes(testTagId);
  const showTestUsersOption =
    !!testTagId && (testUserCount > 0 || isTestUsersSelected);
  const selectedAudienceCount =
    filteredPeopleBySelectedAudienceCount +
    (isTestUsersSelected ? testUserCount : 0);
  const hasStaffRoleChip = visibleSystemRoles.some(
    role => role.id === STAFF_ROLE,
  );
  const testUsersChip = showTestUsersOption ? (
    <Tag
      visual={"accent.blue"}
      mode={isTestUsersSelected ? "dark" : "light"}
      onClick={() =>
        setSelectedPeopleTags(prev =>
          isTestUsersSelected
            ? prev.filter(id => id !== testTagId)
            : [...prev, testTagId],
        )
      }
    >
      {t("TEST_USERS", { count: testUserCount })}
    </Tag>
  ) : null;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [updateBroadcast, { loading: isUpdatingBroadcast }] =
    useBroadcastUpdateByIdMutation();

  const handleClose = () => {
    setErrorMessage(null);
    setSkipEmails(broadcast?.sharedTo?.skipEmails ?? false);
    setSendToType(defaultSendToType);
    setSelectedPeopleTags(defaultSelectedPeopleTags);
    setSelectedSystemRoleIds(defaultSelectedSystemRoleIds);
    setIsTeaserActive(broadcast?.sharedTo?.teaser?.enabled ?? false);
    setTeaserDescription(
      broadcast?.sharedTo?.teaser?.description
        ? getMultilingualString(broadcast.sharedTo.teaser.description)
        : "",
    );
    onClose();
  };

  const handleShareBroadcast = async () => {
    try {
      await updateBroadcast({
        variables: {
          id: broadcast?._id,
          record: {
            status: EnumBroadcastStatus.Published,
            sharedTo: {
              type:
                sendToType === "ALL"
                  ? EnumBroadcastSharedToType.AllPeople
                  : EnumBroadcastSharedToType.PeopleByTag,
              sentToPeople:
                sendToType === "ALL"
                  ? allWhereaboutsPeopleUsers.map(p => p._id)
                  : [],
              sentToPeopleTags:
                sendToType === "BY_TAG" ? selectedPeopleTags : [],
              sentToPeopleSystemRoles:
                sendToType === "BY_TAG" ? selectedSystemRoleIds : [],
              skipEmails: skipEmails,
              teaser: {
                enabled: isTeaserActive,
                description: { en: teaserDescription, fr: "", es: "" },
              },
            },
          },
        },
      });
      onShareSuccess();
      handleClose();
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("SUCCESS"),
      });
    } catch (error) {
      trackPostHogException(error, {
        fileName: "ShareBroadcastModal.tsx",
        method: "handleShareBroadcast",
        message: "Error sharing broadcast",
      });
      setErrorMessage(t("ERROR"));
    }
  };

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={handleClose}
      HeaderIcon={SendDiagonal}
      title={t("TITLE")}
      cancelButtonProps={{ onClick: handleClose }}
      cancelButtonCopy={t("CANCEL")}
      saveButtonProps={{
        onClick: handleShareBroadcast,
        visual: "accent.blue",
        isLoading: isUpdatingBroadcast,
        disabled:
          !sendToType ||
          (sendToType === "BY_TAG" &&
            selectedPeopleTags.length === 0 &&
            selectedSystemRoleIds.length === 0),
      }}
      saveButtonCopy={t("SEND")}
      isMaxZIndex
    >
      <Flex flexDir={"column"} gap={"300"}>
        <Text color={"text.mid"}>{t("DESCRIPTION")}</Text>
        {visiblePeopleTags.length === 0 && visibleSystemRoles.length === 0 && (
          <SimpleFlag visual={"highlight"} text={t("SHARE_BY_TAG_FLAG")} />
        )}
        {initialLoad ? (
          <Spinner width={"32px"} height={"32px"} />
        ) : (
          <Flex flexDir={"column"} gap={"150"}>
            <Text visual={"bodySemiBold"} color={"text.dark"}>
              {t("RECIPIENTS")}
            </Text>

            <RadioGroup
              value={sendToType}
              onValueChange={({ value }: { value: "ALL" | "BY_TAG" }) =>
                setSendToType(value)
              }
            >
              <Radio value={"ALL"}>{t("SHARE_ALL")}</Radio>
              {sendToType === "ALL" && (
                <Text
                  color={"text.accent.blue.mid"}
                  visual={"bodyMedium"}
                  pl={"500"}
                >
                  {t("SHARE_ALL_HINT", {
                    num: allWhereaboutsPeopleUsers.length,
                  })}
                </Text>
              )}
              {(visiblePeopleTags.length > 0 ||
                visibleSystemRoles.length > 0 ||
                showTestUsersOption) && (
                <>
                  <Radio
                    value={"BY_TAG"}
                    helperText={
                      sendToType === "BY_TAG"
                        ? t("SHARE_BY_TAG_HINT")
                        : undefined
                    }
                  >
                    {t("SHARE_BY_TAG")}
                  </Radio>
                  {sendToType === "BY_TAG" && (
                    <>
                      <Flex gap={"100"} flexWrap={"wrap"} pl={"500"}>
                        {!hasStaffRoleChip && testUsersChip}
                        {visibleSystemRoles.map(role => {
                          const roleId =
                            role.id as EnumBroadcastSharedToSentToPeopleSystemRoles;
                          const isSelected =
                            selectedSystemRoleIds.includes(roleId);
                          const handleSelect = () => {
                            if (isSelected) {
                              setSelectedSystemRoleIds(prev =>
                                prev.filter(id => id !== roleId),
                              );
                            } else {
                              setSelectedSystemRoleIds(prev => [
                                ...prev,
                                roleId,
                              ]);
                            }
                          };
                          return (
                            <Fragment key={role.id}>
                              <Tag
                                visual={"accent.blue"}
                                mode={isSelected ? "dark" : "light"}
                                onClick={handleSelect}
                              >
                                {role.label}
                              </Tag>
                              {role.id === STAFF_ROLE && testUsersChip}
                            </Fragment>
                          );
                        })}
                        {visiblePeopleTags.map(tag => {
                          const isSelected = selectedPeopleTags.includes(
                            tag._id,
                          );
                          const handleSelect = () => {
                            if (isSelected) {
                              setSelectedPeopleTags(prev =>
                                prev.filter(id => id !== tag._id),
                              );
                            } else {
                              setSelectedPeopleTags(prev => [...prev, tag._id]);
                            }
                          };
                          return (
                            <Tag
                              key={tag._id}
                              visual={"accent.blue"}
                              mode={isSelected ? "dark" : "light"}
                              onClick={handleSelect}
                            >
                              {getMultilingualString(tag.name)}
                            </Tag>
                          );
                        })}
                      </Flex>
                      {selectedAudienceCount > 0 && (
                        <Text
                          color={"text.accent.blue.mid"}
                          visual={"bodyMedium"}
                          pl={"500"}
                        >
                          {t("SHARE_BY_TAG_RESULTS", {
                            num: selectedAudienceCount,
                          })}
                        </Text>
                      )}
                    </>
                  )}
                </>
              )}
            </RadioGroup>
            <Switch
              checked={!skipEmails}
              onCheckedChange={v => {
                setSkipEmails(!v.checked);
              }}
              label={t("SEND_EMAILS")}
            />
            {teaserEnabled && (
              <Flex flexDir="column" gap="150" pt="100">
                <Switch
                  checked={isTeaserActive}
                  onCheckedChange={v => {
                    setIsTeaserActive(v.checked);
                  }}
                  label={t("TEASER.TOGGLE")}
                />
                {isTeaserActive && (
                  <Flex flexDir="column" gap="100" pl="100">
                    <Text visual="bodyMedium" color="text.mid">
                      {t("TEASER.DESCRIPTION")}
                    </Text>
                    <TextEditor
                      value={teaserDescription}
                      onChange={val => setTeaserDescription(val ?? "")}
                      placeholder={t("TEASER.PLACEHOLDER")}
                    />
                  </Flex>
                )}
              </Flex>
            )}
            {errorMessage && (
              <Text color={"text.error.mid"} visual={"bodyMedium"}>
                {errorMessage}
              </Text>
            )}
          </Flex>
        )}
      </Flex>
    </WhereaboutsModal>
  );
};
